/**
 * t446u: the optional Building field in Create a channel: the same choices as Channel settings › About › Building
 * ("No building", then the workspace's buildings). Computer: a select. Phone: a row that opens a Building sheet over the
 * form (swipe to close, no ✕), like Section.
 */
import { useState } from 'react';
import { Building2, Check, ChevronRight } from 'lucide-react';
import { BUILDING_FIELD_LABEL, buildingChoiceLabel, buildingOptions } from '@tradeoutlet/shared';
import { Field, Select } from '@/components/ui';
import { BottomSheet } from '@/mobile/BottomSheet';
import { useBuildings } from '@/features/buildings/common';

export function BuildingField({ value, onChange, phone }: { value: string | null; onChange: (id: string | null) => void; phone: boolean }) {
  const b = useBuildings();
  const buildings = b.data?.buildings;
  const options = buildingOptions(buildings ?? []);
  const [open, setOpen] = useState(false);
  // Mounted on first open so its sheet sits after (above) the Create a channel sheet, like the Section sheet.
  const [used, setUsed] = useState(false);
  if (phone) return (
    <div data-testid="new-channel-building">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">{BUILDING_FIELD_LABEL}</span>
      <button type="button" onClick={() => { setUsed(true); setOpen(true); }} data-testid="new-channel-building-row" aria-haspopup="dialog" aria-label={`${BUILDING_FIELD_LABEL}: ${buildingChoiceLabel(buildings, value)}`}
        className="flex h-11 w-full items-center gap-2 rounded-md border border-slate-300 px-3 text-left text-base dark:border-slate-700">
        <Building2 className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
        <span className="min-w-0 flex-1 truncate" data-testid="new-channel-building-value">{buildingChoiceLabel(buildings, value)}</span>
        <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
      </button>
      {used && <BottomSheet open={open} onClose={() => setOpen(false)} title={BUILDING_FIELD_LABEL} testId="new-channel-building-sheet">
        <ul className="px-2 pb-2" role="listbox" aria-label={BUILDING_FIELD_LABEL}>
          {options.map(o => (
            <li key={o.id ?? 'none'}>
              <button type="button" role="option" aria-selected={value === o.id} onClick={() => { onChange(o.id); setOpen(false); }}
                className="flex min-h-[44px] w-full items-center gap-2 rounded-md px-3 text-left text-[15px] active:bg-slate-100 dark:active:bg-slate-800" data-testid="building-option">
                <span className="min-w-0 flex-1 truncate">{o.label}</span>
                {value === o.id && <Check className="h-4 w-4 shrink-0" aria-hidden />}
              </button>
            </li>
          ))}
        </ul>
      </BottomSheet>}
    </div>
  );
  return (
    <Field label={BUILDING_FIELD_LABEL}>
      <Select value={value ?? ''} onChange={e => onChange(e.target.value || null)} data-testid="new-channel-building-select" aria-label={BUILDING_FIELD_LABEL}>
        {options.map(o => <option key={o.id ?? 'none'} value={o.id ?? ''}>{o.label}</option>)}
      </Select>
    </Field>
  );
}
